import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  RefreshCw,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Webhook,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebhookDiagnosticsPage = () => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDiagnostic, setSelectedDiagnostic] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchDiagnostics();
  }, [statusFilter]);

  const fetchDiagnostics = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/webhook-diagnostics${params}`);
      setDiagnostics(response.data.diagnostics || []);
    } catch (error) {
      console.error('Failed to fetch diagnostics:', error);
      toast.error('Failed to load webhook diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (diagId) => {
    try {
      const response = await axios.get(`${API}/admin/webhook-diagnostics/${diagId}`);
      setSelectedDiagnostic(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch diagnostic detail:', error);
      toast.error('Failed to load diagnostic details');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all webhook diagnostic records?')) {
      return;
    }
    
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/admin/webhook-diagnostics`);
      toast.success(`Cleared ${response.data.deleted_count} diagnostic records`);
      fetchDiagnostics();
    } catch (error) {
      console.error('Failed to clear diagnostics:', error);
      toast.error('Failed to clear diagnostics');
    } finally {
      setClearing(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      unmatched: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      error: { color: 'bg-red-100 text-red-800', icon: XCircle },
      skipped: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      received: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      non_sms_event: { color: 'bg-gray-100 text-gray-600', icon: Clock }
    };
    
    const variant = variants[status] || variants.received;
    const Icon = variant.icon;
    
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status || 'unknown'}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    return formatDateTime(timestamp);
  };

  const extractSmsInfo = (diag) => {
    const body = diag.payload?.body || {};
    return {
      from: body.from?.phoneNumber || 'N/A',
      to: body.to?.[0]?.phoneNumber || 'N/A',
      message: body.subject || 'N/A',
      direction: body.direction || 'N/A'
    };
  };

  return (
    <div className="space-y-6" data-testid="webhook-diagnostics-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Webhook className="w-6 h-6" />
              Webhook Diagnostics
            </h1>
            <p className="text-muted-foreground">
              Debug incoming RingCentral webhook payloads
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="unmatched">Unmatched</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
              <SelectItem value="received">Received</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchDiagnostics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="destructive" onClick={clearAll} disabled={clearing}>
            {clearing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 mr-2" />
            )}
            Clear All
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Webhooks</CardTitle>
          <CardDescription>
            Last {diagnostics.length} webhook events received from RingCentral
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : diagnostics.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Webhook className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No webhook events recorded yet</p>
              <p className="text-sm mt-1">Send an SMS to your RingCentral number to test</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {diagnostics.map((diag) => {
                  const smsInfo = extractSmsInfo(diag);
                  return (
                    <TableRow key={diag.id}>
                      <TableCell className="font-mono text-sm">
                        {formatTimestamp(diag.timestamp)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(diag.processing_status)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {smsInfo.from}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {smsInfo.message}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{smsInfo.direction}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetail(diag.id)}
                          data-testid={`view-detail-${diag.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Webhook Detail: {selectedDiagnostic?.id}
            </DialogTitle>
            <DialogDescription>
              {selectedDiagnostic && formatTimestamp(selectedDiagnostic.timestamp)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedDiagnostic && (
            <div className="space-y-4">
              {/* Status */}
              <div>
                <h4 className="font-semibold mb-2">Processing Status</h4>
                {getStatusBadge(selectedDiagnostic.processing_status)}
              </div>

              {/* Processing Details */}
              {selectedDiagnostic.processing_details?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Processing Steps</h4>
                  <div className="space-y-2">
                    {selectedDiagnostic.processing_details.map((detail, idx) => (
                      <div key={idx} className="bg-muted p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{detail.step}</Badge>
                          {detail.result && <Badge>{detail.result}</Badge>}
                        </div>
                        <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                          {JSON.stringify(detail, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Full Payload */}
              <div>
                <h4 className="font-semibold mb-2">Full Webhook Payload</h4>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto max-h-[300px] overflow-y-auto">
                  {JSON.stringify(selectedDiagnostic.payload, null, 2)}
                </pre>
              </div>

              {/* Request Headers */}
              <div>
                <h4 className="font-semibold mb-2">Request Headers</h4>
                <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto max-h-[200px] overflow-y-auto">
                  {JSON.stringify(selectedDiagnostic.headers, null, 2)}
                </pre>
              </div>

              {/* Source IP */}
              <div>
                <h4 className="font-semibold mb-2">Source IP</h4>
                <code className="bg-muted px-2 py-1 rounded">{selectedDiagnostic.source_ip}</code>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhookDiagnosticsPage;
